
import { useState, useRef, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Ticket } from "@/types";
import { Scan, Camera, StopCircle } from "lucide-react";
import { subDays } from "date-fns";
import { useToast } from "@/components/ui/use-toast";

// Mock tickets for simulation
const generateMockTicket = (id: string): Ticket => ({
  id,
  eventId: "1",
  userId: `u${id}`,
  purchaseDate: subDays(new Date(), 5).toISOString(),
  status: "valid",
  qrCode: `qr-code-${id}`
});

const MOCK_TICKETS: Ticket[] = [
  generateMockTicket("t1"),
  generateMockTicket("t2")
];

interface ScannerSimulationProps {
  onScan: (ticket: Ticket) => void;
}

export const ScannerSimulation = ({ onScan }: ScannerSimulationProps) => {
  const [scanning, setScanning] = useState(false);
  const [usingCamera, setUsingCamera] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const { toast } = useToast();

  // Clean up camera stream when component unmounts
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const simulateScan = async () => {
    setScanning(true);
    // Simulate scanning delay
    await new Promise(resolve => setTimeout(resolve, 1500));
    // Randomly select a ticket for simulation
    const randomTicket = MOCK_TICKETS[Math.floor(Math.random() * MOCK_TICKETS.length)];
    onScan(randomTicket);
    setScanning(false);
  };

  const startCamera = async () => {
    try {
      // Stop any existing streams first
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      
      // Request camera access
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } // Use back camera if available
      });
      
      streamRef.current = stream;
      
      // Make sure we have a valid video element before proceeding
      if (!videoRef.current) {
        throw new Error("Video element not found");
      }
      
      // Set the stream as the video source
      videoRef.current.srcObject = stream;
      videoRef.current.muted = true;
      
      // Wait for the video to be ready to play
      videoRef.current.onloadedmetadata = () => {
        if (videoRef.current) {
          // Play the video after metadata is loaded
          videoRef.current.play()
            .then(() => {
              setUsingCamera(true);
              toast({
                title: "Camera activated",
                description: "Point the camera at a QR code to scan.",
              });
            })
            .catch(err => {
              console.error("Error playing video:", err);
              toast({
                title: "Camera error",
                description: "Could not start video playback. Please try again.",
                variant: "destructive"
              });
            });
        }
      };
    } catch (error) {
      console.error("Error accessing camera:", error);
      toast({
        title: "Camera error",
        description: "Could not access your camera. Please check permissions.",
        variant: "destructive"
      });
      setUsingCamera(false);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    
    setUsingCamera(false);
  };

  const verifyScannedTicket = () => {
    // In a real implementation, this would use a QR code scanning library
    // to detect and read the QR code from the video stream
    // For this demo, we'll simulate a successful scan
    setScanning(true);
    setTimeout(() => {
      const randomTicket = MOCK_TICKETS[Math.floor(Math.random() * MOCK_TICKETS.length)];
      onScan(randomTicket);
      setScanning(false);
    }, 1000);
  };

  return (
    <div className="space-y-4">
      <div className="relative w-full aspect-video bg-black rounded-lg overflow-hidden">
        {usingCamera ? (
          <>
            <video 
              ref={videoRef} 
              autoPlay 
              playsInline
              muted
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-48 h-48 border-2 border-blue-500 rounded-lg opacity-70"></div>
            </div>
          </>
        ) : (
          <div className={`absolute inset-0 flex items-center justify-center ${scanning ? 'animate-pulse' : ''}`}>
            <Scan className="w-16 h-16 text-primary/50" />
          </div>
        )}
        
        {scanning && !usingCamera && (
          <div className="absolute inset-0 bg-gradient-to-b from-primary/10 to-transparent animate-scan" />
        )}
      </div>
      
      <div className="flex flex-col space-y-2">
        {!usingCamera ? (
          <>
            <Button 
              onClick={startCamera} 
              className="w-full"
              disabled={scanning}
            >
              <Camera className="mr-2 h-4 w-4" />
              Use Camera
            </Button>
            <Button 
              onClick={simulateScan} 
              className="w-full"
              disabled={scanning}
            >
              {scanning ? "Scanning..." : "Simulate Scan"}
            </Button>
          </>
        ) : (
          <>
            <Button 
              onClick={verifyScannedTicket} 
              className="w-full"
              disabled={scanning}
            >
              {scanning ? "Scanning..." : "Verify Ticket"}
            </Button>
            <Button 
              onClick={stopCamera} 
              variant="outline"
              className="w-full"
            >
              <StopCircle className="mr-2 h-4 w-4" />
              Stop Camera
            </Button>
          </>
        )}
      </div>
      
      <p className="text-sm text-gray-400 text-center">
        {usingCamera 
          ? "Position the QR code within the blue box and click Verify Ticket" 
          : "Point the camera at a patron's QR code to verify their ID"}
      </p>
    </div>
  );
};
